import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';

import { Button, Card, InfoCard, Modal, NavigationItem } from '@/components';
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    ChevronDownIcon,
} from '@/components/Icons';
import { SelectCaregiver } from '@/features/booking/widgets/select-caregiver/SelectCaregiver';
import { CaregiverAvatar } from '@/features/booking/shared/ui/CaregiverAvatar';
import {
    bookingErrorAtom,
    updateDraftAtom,
} from '@/features/booking/shared/model/bookingState';
import { useIsSupportedDevice } from './model/useIsSupportedDevice';
import { usePreferredCaregiver } from '@/features/booking/shared/model/usePreferredCaregiver';
import { useWaitingTime } from './model/useWaitingTime';
import { R } from '@/features/booking/resources/Texts.resx';
import { format as formatString } from '@/knockoutDeprecated/viewModels/consultations/ConsultationResources.resx';
import { Utils } from '@/shared/Utils';
import { CaregiverSelectionStatus, ProductDto } from '@/shared/interfaces';
import { useCreateConsultation } from '@/features/booking/shared/model/useCreateConsultation';
import { isSubmittingConsultationAtom } from '@/features/booking/shared/model/latestActions';
import { useTimeslots } from '@/features/booking/shared/model/useTimeslots';
import { TimeslotGrid } from '@/features/booking/widgets/book-or-queue-up/ui/TimeslotGrid';

export const BookOrQueueUp = ({
    product,
    userUid,
}: {
    product: ProductDto;
    userUid: string;
}) => {
    const consultationType = product.ConsultationType;
    const [isCaregiverModalOpen, setIsCaregiverModalOpen] = useState(false);
    const updateSessionState = useSetAtom(updateDraftAtom);
    const [_, setErrorMessage] = useAtom(bookingErrorAtom);

    const { openVideoConsultation } = useCreateConsultation(userUid);
    const isSubmitting = useAtomValue(isSubmittingConsultationAtom);

    const onSubmit = ({ isQueue }: { isQueue: boolean }) => {
        void openVideoConsultation({ isQueue });
    };

    const {
        hasAvailableTimeslots,
        visibleTimeslotsPerDay,
        isFetching: isFetchingTimeslots,

        selectNextDay,
        selectPreviousDay,

        selectedTimeSlot,
        setSelectedTimeSlot,
        error: timeSlotsError,
    } = useTimeslots({ consultationType });

    const {
        waitingTimeFormatted,
        isLongWaitingTime,
        canQueueNow,
        isQueueOpen,
        isFetching: isFetchingWaitingTime,
        error: waitingTimeError,
    } = useWaitingTime(consultationType, product.IsQueueEnabled);

    const canSelectCaregiver =
        product.CaregiverSelectionStatus != CaregiverSelectionStatus.Disabled;

    const {
        caregivers,
        hasPreferredCaregiver,
        selectedCaregiver,
        isLoading: isLoadingCaregiver,
        error: caregiverError,
    } = usePreferredCaregiver({
        consultationType: product.ConsultationType,
        performInitialFetch: canSelectCaregiver,
    });
    const shouldShowCaregiverSelector =
        canSelectCaregiver &&
        (caregivers.length > 1 ||
            product.CaregiverSelectionStatus ===
                CaregiverSelectionStatus.ForceSelection);

    // reset queue status (if user navigates back from queue)
    useEffect(() => {
        updateSessionState({ queue: '' });
    }, []);

    useEffect(() => {
        if (timeSlotsError) {
            setErrorMessage(R.ErrorFetchingTimetable);
            return;
        }
        if (waitingTimeError) {
            setErrorMessage(R.ErrorFetchingWaitingTime);
            return;
        }
        if (caregiverError) {
            setErrorMessage(R.ErrorFetchingCaregivers);
            return;
        }
    }, [waitingTimeError, timeSlotsError, caregiverError]);

    const hasDeterminedQueueStatus =
        !isFetchingWaitingTime && !isLoadingCaregiver;

    const showTimeZoneWarning = Utils.isDifferentTimezone();

    const timeZoneWarning = formatString(
        R.TimeZoneWarning,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
    );

    const isSupportedDevice = useIsSupportedDevice();

    const isQueueNowButtonEnabled = () => {
        if (hasPreferredCaregiver) return false;
        // we enable button in opening hours and also if opened for queueing (potentially before workhours)
        return isQueueOpen || canQueueNow;
    };

    const isQueueNowVisible = () => {
        if (hasPreferredCaregiver) return false;
        return canQueueNow;
    };

    const isPreviousDayButtonEnabled =
        selectPreviousDay && !isFetchingTimeslots;
    const isNextDayButtonEnabled = selectNextDay && !isFetchingTimeslots;

    const handleBookWithTimeslot = () => {
        if (!selectedTimeSlot) {
            setErrorMessage(R.PleaseChooseDate);
            return;
        }
        onSubmit({ isQueue: false });
    };

    const handleQueueUp = () => {
        updateSessionState({ queue: 'true' });
        onSubmit({ isQueue: true });
    };

    const closeCaregiverModal = () => {
        setIsCaregiverModalOpen(false);
    };

    return (
        <Card alwaysCard={false}>
            <h2 className="mb-6">{R.BookOrQueueUpHeader}</h2>

            {/* Select caregiver */}
            {shouldShowCaregiverSelector && (
                <>
                    <NavigationItem
                        onClick={() => setIsCaregiverModalOpen(true)}
                        caption={
                            !hasPreferredCaregiver
                                ? R.AllCaregivers
                                : selectedCaregiver?.FullName
                        }
                        icon={
                            hasPreferredCaregiver ? (
                                <CaregiverAvatar
                                    photoUrl={selectedCaregiver?.PhotoUrl}
                                />
                            ) : null
                        }
                        rightIcon={<ChevronDownIcon />}
                        className={`flex cursor-pointer flex-row items-center rounded-2xl p-4 hover:bg-gray-100 ${!hasPreferredCaregiver ? '!min-h-14' : ''}`}
                    />

                    {isCaregiverModalOpen && (
                        <Modal
                            hideCloseButton={true}
                            onClose={closeCaregiverModal}
                        >
                            <SelectCaregiver
                                product={product}
                                onSubmitCaregiver={closeCaregiverModal}
                                asCard={false}
                            />
                        </Modal>
                    )}
                </>
            )}

            {!isSupportedDevice && (
                <InfoCard>
                    <span className="mt-2">{R.UnsupportedDeviceVideo}</span>
                </InfoCard>
            )}

            {/* Queue */}
            {product.IsQueueEnabled && (
                <div className="mb-6">
                    {!isQueueOpen &&
                        !canQueueNow &&
                        hasDeterminedQueueStatus && (
                            <InfoCard>
                                <span className="mt-2">
                                    {R.OutsideOpeningHours}
                                </span>
                            </InfoCard>
                        )}

                    {isQueueNowVisible() && (
                        <div className="bg-action-extralight flex flex-col gap-6 rounded-2xl p-6">
                            {R.QueueUpDescription}
                            <div className="flex flex-row justify-between gap-4">
                                <span
                                    className={`${isLongWaitingTime ? 'text-action-red' : ''}`}
                                    dangerouslySetInnerHTML={{
                                        __html: waitingTimeFormatted,
                                    }}
                                ></span>
                                <Button
                                    className="ns:min-w-44 min-w-32"
                                    onClick={handleQueueUp}
                                    variant="secondary"
                                    disabled={!isQueueNowButtonEnabled()}
                                    isSubmitting={isSubmitting}
                                >
                                    {R.QueueUpButtonText}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {showTimeZoneWarning && (
                <InfoCard>
                    <span className="mb-2">{timeZoneWarning}</span>
                </InfoCard>
            )}

            {/* Available timeslots state */}
            {(!!hasAvailableTimeslots || isFetchingTimeslots) && (
                <>
                    {/* Toggle timeslot day buttons */}
                    <div className="my-4 flex w-full flex-row items-center justify-between">
                        <button
                            className="text-action-dark hover:text-action-dark enabled:cursor-pointer disabled:text-gray-300"
                            disabled={!isPreviousDayButtonEnabled}
                            onClick={() => selectPreviousDay()}
                        >
                            <ArrowLeftIcon className="h-8 w-8" />
                        </button>

                        <span className="leading-tight font-medium first-letter:uppercase">
                            {visibleTimeslotsPerDay?.day ?? R.Today}
                        </span>

                        <button
                            className="text-action hover:text-action-dark enabled:cursor-pointer disabled:text-gray-300"
                            disabled={!isNextDayButtonEnabled}
                            onClick={() => selectNextDay()}
                        >
                            <ArrowRightIcon className="h-8 w-8" />
                        </button>
                    </div>

                    <TimeslotGrid
                        isFetchingTimeslots={isFetchingTimeslots}
                        timeslots={visibleTimeslotsPerDay}
                        selectedTimeslot={selectedTimeSlot}
                        onChangeSelectedTimeslot={setSelectedTimeSlot}
                    />

                    {/* Submit button */}
                    <div className="flex w-full flex-row justify-end">
                        <Button
                            variant="primary"
                            disabled={isSubmitting}
                            onClick={handleBookWithTimeslot}
                            className="ns:w-auto mt-2 w-full min-w-44"
                            isSubmitting={isSubmitting}
                        >
                            {R.BookConsultation}
                        </Button>
                    </div>

                    {/* Terms of service*/}
                    <div className="text-sm">
                        {R.TermsOfService1}
                        <a
                            className="text-action hover:text-action-dark border-0 bg-transparent whitespace-nowrap no-underline"
                            href="https://app.hejdoktor.dk/handelsbetingelser/"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            &nbsp;{R.TermsOfService2}
                        </a>
                    </div>
                </>
            )}

            {!hasAvailableTimeslots && !isFetchingTimeslots && (
                <Card className="bg-bg-subtle!">
                    <h2 className="mb-6">{R.NoAvailableTimeslotsHeader}</h2>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: Utils.decodeHtml(
                                R.NoAvailableTimeslotsDescription,
                            ),
                        }}
                    />
                </Card>
            )}
        </Card>
    );
};
